import type { ComponentChildren } from "preact";

/**
 * 滾輪選單，上下滑動選一個值，不用叫出鍵盤。
 *
 * 不用原生 `<select>`：它在 iPhone 上不一定是滾輪，要看系統版本。
 *
 * **不受控，值存在元素的 `data-index`**，理由與其他競猜欄位相同：重繪不能重置還沒
 * 送出的答案。分頁切走時面板是 `display:none`，瀏覽器會把捲動位置歸零而且不發事件，
 * 所以回來時要由 `data-index` 捲回去，不能反過來由捲動位置決定值。
 */

const itemHeight = (wheel: HTMLElement) =>
  wheel.querySelector<HTMLElement>(".wheel-item")?.offsetHeight ?? 0;

function select(wheel: HTMLElement, options: readonly string[], index: number) {
  wheel.dataset.index = String(index);
  wheel.setAttribute("aria-valuenow", String(index));
  wheel.setAttribute("aria-valuetext", options[index]);
}

/** ref 每次重繪都會跑。值有變才採用新的初始值；看不見時量不到高度，先不捲。 */
function sync(wheel: HTMLElement, options: readonly string[], initial: number) {
  if (wheel.dataset.initial !== String(initial)) {
    wheel.dataset.initial = String(initial);
    select(wheel, options, initial);
  }
  const height = itemHeight(wheel);
  if (!height) return;
  const target = Number(wheel.dataset.index) * height;
  if (Math.abs(wheel.scrollTop - target) > height / 2) wheel.scrollTop = target;
}

export function Wheel({ id, label, unit, options, initial }: {
  id: string;
  label: string;
  unit: ComponentChildren;
  options: readonly string[];
  initial: number;
}) {
  const moveTo = (wheel: HTMLElement, index: number) => {
    const next = Math.min(options.length - 1, Math.max(0, index));
    select(wheel, options, next);
    wheel.scrollTop = next * itemHeight(wheel);
  };
  return (
    <div class="wheel-field">
      <div class="wheel-frame">
        <div
          id={id} class="wheel" tabIndex={0} role="spinbutton" aria-label={label}
          aria-valuemin={0} aria-valuemax={options.length - 1}
          ref={element => { if (element) sync(element, options, initial); }}
          onScroll={event => {
            const wheel = event.currentTarget as HTMLElement;
            const height = itemHeight(wheel);
            if (!height) return;
            select(wheel, options, Math.min(options.length - 1, Math.round(wheel.scrollTop / height)));
          }}
          onKeyDown={event => {
            if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
            event.preventDefault();
            const wheel = event.currentTarget as HTMLElement;
            moveTo(wheel, Number(wheel.dataset.index) + (event.key === "ArrowDown" ? 1 : -1));
          }}
        >
          {options.map((option, index) => (
            <div
              key={option} class="wheel-item"
              onClick={event => moveTo((event.currentTarget as HTMLElement).parentElement as HTMLElement, index)}
            >{option}</div>
          ))}
        </div>
      </div>
      <span>{unit}</span>
    </div>
  );
}

/** 滾輪目前選到的值。 */
export function wheelValue(form: HTMLFormElement, id: string, options: readonly string[]): string {
  const wheel = form.querySelector<HTMLElement>(`#${id}`);
  return options[Number(wheel?.dataset.index ?? 0)] ?? "";
}
